import { createContext, useContext } from "react";

/**
 * Resolved variable values for an overlay (e.g. `{ streamerName: "Cris" }`).
 * Consumed by Text element render and variant renders to interpolate
 * `{varName}` placeholders inside user-authored strings.
 *
 * Special key `__previewMode`: when set to `true`, the interpolator
 * substitutes unresolved tokens (e.g. `{user.something}`) with a
 * friendly placeholder derived from the path's last segment instead of
 * leaving the literal `{token}` text on screen. Used by Store / Builder
 * preview components so authors see realistic mockups while editing.
 */
export type OverlayVariables = Record<string, unknown>;

/** Reserved key on `OverlayVariables` to enable placeholder substitution. */
export const PREVIEW_MODE_KEY = "__previewMode";

export const EMPTY_VARS: OverlayVariables = Object.freeze({});

export const OverlayVariablesContext =
  createContext<OverlayVariables>(EMPTY_VARS);

export const useOverlayVariables = (): OverlayVariables => {
  return useContext(OverlayVariablesContext);
};

// Walks a dot-path (`user.name`, `last.donation.amount`) into the variables
// object. Returns `undefined` if any segment is missing or non-traversable;
// callers decide how to handle the miss (preserve literal vs. empty string).
const resolvePath = (vars: OverlayVariables, path: string): unknown => {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[key];
  }, vars);
};

// Humanises a dot-path's last segment for placeholder display:
// `user.username` → `Username`, `last.sub.gifterName` → `Gifter Name`.
const humanisePath = (path: string): string => {
  const last = path.split(".").pop() ?? path;
  // Insert spaces before camelCase boundaries, then title-case.
  const spaced = last.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Replaces `{varName}` and `{a.b.c}` placeholders with their string value from
// `vars`. Plain identifiers (no dot) keep working as before — the dot-path
// regex accepts them as a subset.
// Undefined / unresolved paths are left as-is (literal `{...}` preserved) by
// default. When `vars.__previewMode === true`, unresolved paths are replaced
// with a humanised version of the last segment so Store / Builder previews
// show readable placeholders instead of curly-brace tokens.
// `null` becomes the empty string; everything else goes through `String()`.
export const interpolateVariables = (
  template: string,
  vars: OverlayVariables,
): string => {
  if (!template) return template;
  const previewMode = vars[PREVIEW_MODE_KEY] === true;
  return template.replace(
    /\{([a-zA-Z_$][\w$]*(?:\.[a-zA-Z_$][\w$]*)*)\}/g,
    (match, path: string) => {
      const value = resolvePath(vars, path);
      if (value === undefined) {
        return previewMode ? humanisePath(path) : match;
      }
      if (value === null) return "";
      return String(value);
    },
  );
};
